import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DATA_QUALITY_PATH, DATA_QUALITY_ROUTE, DEFAULT_PAGE_SIZE, pageKindLineText, recordHref, t, viewerScopeKey, type ColumnDef, type DataReviewRow, type PageSize, type SortState } from '@bms2/shared';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { PageModule } from '../../shell/PageModule';
import { useRows } from './api';
import styles from './DataReviewPage.module.css';

/** 🔨 Every table sits inside the List Viewer (piece 4); the records needing review are one, newest first. */
const COLUMNS: readonly ColumnDef[] = [
  { key: 'label', label: t('recordChecks.review.record'), role: 'primary', type: 'text' },
  { key: 'suggestions', label: t('recordChecks.review.suggestions'), role: 'value', type: 'number' },
  { key: 'newestAt', label: t('recordChecks.review.newest'), role: 'date', type: 'instant' },
];

/**
 * 🔒 Records Needing Data Review at `/data-quality` (piece 12's
 * `queue:data-quality`; decision 5382): each Person and Company whose newest
 * mark on a field is a Differs nobody has accepted or dismissed, newest first,
 * with how many such suggestions it holds. A row opens the record, whose
 * Suggestions tile is where each is decided.
 */
export function DataReviewPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const { state, reload } = useRows<DataReviewRow>(DATA_QUALITY_PATH, apiBaseUrl);
  const [sort, setSort] = useState<SortState | null>({ key: 'newestAt', direction: 'desc' });
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  return (
    <PageModule title={t('recordChecks.review.title')} subtitle={pageKindLineText({ page: 'named', route: DATA_QUALITY_ROUTE })}>
      <div data-page="data-review" className={styles.page}>
        <p className={styles.intro}>{t('recordChecks.review.intro')}</p>
        <AsyncStatePanel state={state} emptyAction={t('recordChecks.review.empty')} onRetry={reload}>
          {(rows) => (
            <div data-data-review-rows={rows.length}>
              <ListViewer
                viewStore={{ scopeKey: viewerScopeKey('data-review'), apiBaseUrl }}
                recordType={t('recordChecks.review.title')}
                views={[{ id: 'all', name: t('recordChecks.review.title') }]}
                currentViewId="all"
                onViewChange={() => undefined}
                columns={COLUMNS}
                rows={rows}
                idOf={(row) => `${row.recordType}:${row.recordId}`}
                read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
                sort={sort}
                onSortChange={setSort}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                emptyAction={t('recordChecks.review.empty')}
                onNavigate={(row) => navigate(recordHref(row.recordType, row.recordId))}
              />
            </div>
          )}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}
